// src/Hooks/usePermissions.js
import { useState, useEffect, useCallback } from 'react';

const getPermissionsFromStorage = () => {
  const permissionsRaw = localStorage.getItem('Permissions');
  let userPermissions = [];

  try {
    userPermissions = permissionsRaw ? JSON.parse(permissionsRaw) : [];
  } catch (e) {
    userPermissions = [];
  }

  const hasPermission = (permName) =>
    userPermissions.some(
      (perm) =>
        perm.PermissionName?.trim().toLowerCase() === permName?.trim().toLowerCase()
    );

  return {
    isAuthenticated: hasPermission('Account.Update'),
    isOrgAuthenticated: hasPermission('Organization_Setting'),
    isInitiateExit: hasPermission('Exit.Manage'),
    employeeCreate: hasPermission('Employee.Create'),
    isAddTicket: hasPermission('Ticket.Add'),
    isAlbum: hasPermission('Album.Create'),
    isPolicy: hasPermission('Policy.Create'),
    isPolicyUpdate: hasPermission('Policy.Update'),
    isVersion: hasPermission('Version.Create'),
    isAwardCreate: hasPermission('Award.Create'),
    isAwardUpdate: hasPermission('Award.Update'),
    isAnnouncementCreate: hasPermission('Announcement.Create'),
    isAnnouncementUpdate: hasPermission('Announcement.Update'),
    isArticle: hasPermission('Edit Article'),
  };
};

// Safe utility to get raw system alerts array from localStorage
export const getRawSystemAlertsArray = () => {
  const alertsRaw = localStorage.getItem('SystemAlerts');
  let systemAlerts = [];

  try {
    const parsed = alertsRaw ? JSON.parse(alertsRaw) : null;
    // Ensure we always return an array
    systemAlerts = Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    systemAlerts = [];
  }

  return systemAlerts;
};

// Safe utility to get raw permissions array from localStorage
export const getRawPermissionsArray = () => {
  const permissionsRaw = localStorage.getItem('Permissions');
  let userPermissions = [];

  try {
    const parsed = permissionsRaw ? JSON.parse(permissionsRaw) : null;
    // Ensure we always return an array
    userPermissions = Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    userPermissions = [];
  }

  return userPermissions;
};

export const usePermissions = () => {
  const [permissions, setPermissions] = useState(getPermissionsFromStorage);
  const [loading, setLoading] = useState(true);

  const syncPermissions = useCallback(() => {
    setLoading(true);
    setPermissions(getPermissionsFromStorage());
    setLoading(false);
  }, []);

  useEffect(() => {
    let mounted = true;

    const handlePermissionsUpdate = () => {
      if (!mounted) return;
      syncPermissions();
    };

    handlePermissionsUpdate();
    window.addEventListener('storage', handlePermissionsUpdate);
    window.addEventListener('authStateChanged', handlePermissionsUpdate);
    window.addEventListener('logout', handlePermissionsUpdate);

    return () => {
      mounted = false;
      window.removeEventListener('storage', handlePermissionsUpdate);
      window.removeEventListener('authStateChanged', handlePermissionsUpdate);
      window.removeEventListener('logout', handlePermissionsUpdate);
    };
  }, [syncPermissions]);

  return { ...permissions, loading };
};